/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { tokens } from '@neo4j-ndl/base';
import classNames from 'classnames';
import { type JSX, type ReactNode } from 'react';
import { useMemo } from 'react';
import {
  type ClearIndicatorProps,
  type CommonPropsAndClassName,
  components,
  type ControlProps,
  type DropdownIndicatorProps,
  type GroupBase,
  type IndicatorsContainerProps,
  type InputProps,
  type MenuListProps,
  type MenuPlacement,
  type MenuPosition,
  type MenuProps,
  type MultiValueProps,
  type OptionProps,
  type PlaceholderProps,
  type SingleValueProps,
  type ValueContainerProps,
} from 'react-select';

/**
 * react-select does not re-export MenuPortalProps from its public API.
 * The deep import path (react-select/dist/declarations/src/components/Menu)
 * is incompatible with moduleResolution:"bundler".
 *
 * Defined locally using publicly exported react-select types until upstream
 * merges the export: https://github.com/JedWatson/react-select/pull/6007
 */
interface MenuPortalProps<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> extends CommonPropsAndClassName<Option, IsMulti, Group> {
  appendTo: HTMLElement | undefined;
  children: ReactNode;
  controlElement: HTMLDivElement | null;
  innerProps: JSX.IntrinsicElements['div'];
  menuPlacement: MenuPlacement;
  menuPosition: MenuPosition;
}

import { Checkbox } from '../checkbox';
import { ChevronDownIconOutline } from '../icons/generated/heroIcons/ChevronDownIconOutline';
import { XMarkIconOutline } from '../icons/generated/heroIcons/XMarkIconOutline';
import { Tag } from '../tag';
import { useNeedleTheme } from '../theme';
import { type SelectOverrideCustomProps } from './types';

/**
 * React-select triggers control/indicator interactions on mouseDown which breaks WCAG 2.5.2 Pointer Cancellation.
 * This helper shifts mouseDown to mouseUp to allow users to cancel by dragging away.
 */
const shiftMouseDownToMouseUp = (
  innerProps?: React.HTMLAttributes<HTMLDivElement>,
  stopPropagation = false,
): React.HTMLAttributes<HTMLDivElement> => {
  const baseProps = innerProps ?? {};
  const maybeStopPropagation = (e: React.SyntheticEvent) =>
    stopPropagation && e.stopPropagation();

  return {
    ...baseProps,
    onMouseDown: (e) => {
      e.preventDefault();
      maybeStopPropagation(e);
    },
    onMouseUp: (e) => {
      baseProps.onMouseUp?.(e);
      baseProps.onMouseDown?.(e);
      maybeStopPropagation(e);
    },
    onTouchEnd: (e) => {
      baseProps.onTouchEnd?.(e);
      baseProps.onMouseDown?.(e as unknown as React.MouseEvent<HTMLDivElement>);
      maybeStopPropagation(e);
    },
    onTouchStart: (e) => {
      e.preventDefault();
      maybeStopPropagation(e);
    },
  };
};

/**
 * React-select triggers option selection on click which fires on mouseDown.
 * This helper shifts the click handler to mouseUp/touchEnd to allow users to cancel by dragging away.
 */
const shiftOptionClickToMouseUp = (
  innerProps?: React.HTMLAttributes<HTMLDivElement>,
): React.HTMLAttributes<HTMLDivElement> => {
  const baseProps = innerProps ?? {};

  return {
    ...baseProps,
    // Remove onClick to prevent double-execution (onMouseUp already calls it, and browser would fire click after mouseup)
    onClick: undefined,
    onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    onMouseUp: (event: React.MouseEvent<HTMLDivElement>) => {
      baseProps.onMouseUp?.(event);
      baseProps.onClick?.(event);
    },
    onTouchEnd: (event: React.TouchEvent<HTMLDivElement>) => {
      baseProps.onTouchEnd?.(event);
      baseProps.onClick?.(event as unknown as React.MouseEvent<HTMLDivElement>);
    },
    onTouchStart: (event: React.TouchEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
  };
};

export const DropdownIndicatorCurrying = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component(
    props: DropdownIndicatorProps<OptionType, IsMulti, GroupType>,
  ) {
    const classes = classNames('ndl-select-dropdown-indicator', {
      'ndl-disabled': props.isDisabled,
      'ndl-focused': props.isFocused,
      'ndl-is-multi': props.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });

    // stopPropagation so that we prevent the control from also toggling the menu (double-toggle)
    const indicatorInnerProps = shiftMouseDownToMouseUp(props.innerProps, true);

    return (
      <components.DropdownIndicator
        {...props}
        className={classes}
        innerProps={indicatorInnerProps}
      >
        <ChevronDownIconOutline
          className="ndl-select-icon"
          style={{
            transform: props.selectProps.menuIsOpen
              ? 'rotate(180deg)'
              : 'rotate(0deg)',
            transitionDuration: tokens.motion.duration.quick,
          }}
        />
      </components.DropdownIndicator>
    );
  };

export const CustomClearIndication = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    className,
    innerProps,
    ...restProps
  }: ClearIndicatorProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames('ndl-select-clear', className, {
      'ndl-focused': restProps.isFocused,
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });

    const handleClear = () => {
      restProps.clearValue?.();
    };

    // Stop propagation to prevent react-select's keyboard handling.
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === '') {
        event.preventDefault();
      }
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation?.();
    };

    // Prevent react-select's default clear-on-mousedown/touchend behavior.
    const preventAndStop = (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const safeInnerProps: typeof innerProps = {
      ...innerProps,
      'aria-hidden': undefined, // Remove to allow focusable button inside
      onMouseDown: preventAndStop,
      onMouseUp: preventAndStop,
      onTouchEnd: preventAndStop,
    };

    return (
      <components.ClearIndicator
        className={classes}
        innerProps={safeInnerProps}
        {...restProps}
      >
        <button
          type="button"
          className="ndl-select-clear-button"
          onClick={handleClear}
          onKeyDown={handleKeyDown}
          aria-label="Clear selection"
        >
          <XMarkIconOutline />
        </button>
        <div className="ndl-select-divider" />
      </components.ClearIndicator>
    );
  };

export const CustomMultiValue = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: MultiValueProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames('ndl-select-multi-value', className, {
      'ndl-disabled': restProps.isDisabled,
      'ndl-focused': restProps.isFocused,
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });
    return (
      <components.MultiValueContainer
        {...restProps}
        innerProps={{
          className: classes,
        }}
      >
        <Tag
          size={size}
          onClick={(e) => {
            e.preventDefault();
            restProps.removeProps.onClick?.(
              e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>,
            );
          }}
          htmlAttributes={{
            onMouseDown: (e) => e.preventDefault(),
          }}
        >
          {children}
        </Tag>
      </components.MultiValueContainer>
    );
  };

export const CustomPlaceholder = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: PlaceholderProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames('ndl-select-placeholder', className, {
      'ndl-disabled': restProps.isDisabled,
      'ndl-focused': restProps.isFocused,
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });
    return (
      <components.Placeholder {...restProps} className={classes}>
        {children}
      </components.Placeholder>
    );
  };

export const CustomControl = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
  isClean,
  errorText,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({ children, className, ...restProps }: ControlProps) {
    const classes = classNames('ndl-select-control', className, {
      'ndl-clean': isClean,
      'ndl-disabled': restProps.isDisabled,
      'ndl-focused': restProps.isFocused,
      'ndl-has-error': !!errorText,
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });

    const controlInnerProps = shiftMouseDownToMouseUp(restProps.innerProps);

    return (
      <components.Control
        {...restProps}
        className={classes}
        innerProps={controlInnerProps}
      >
        {children}
      </components.Control>
    );
  };

export const CustomIndicatorsContainer = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: IndicatorsContainerProps) {
    const classes = classNames('ndl-select-indicators-container', className, {
      'ndl-disabled': restProps.isDisabled,
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });
    return (
      <components.IndicatorsContainer {...restProps} className={classes}>
        {children}
      </components.IndicatorsContainer>
    );
  };

export const CustomValueContainer = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: ValueContainerProps) {
    const classes = classNames('ndl-select-value-container', className, {
      'ndl-disabled': restProps.isDisabled,
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });
    return (
      <components.ValueContainer {...restProps} className={classes}>
        {children}
      </components.ValueContainer>
    );
  };

export const CustomInput = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
  errorText,
  errorTextId,
  helpText,
  helpTextId,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({ className, ...restProps }: InputProps) {
    const classes = classNames('ndl-select-input', className, {
      'ndl-disabled': restProps.isDisabled,
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });

    const defaultAriaDescribedBy = restProps['aria-describedby'];

    const combinedAriaDescribedBy = useMemo(() => {
      const ariaDescribedBy: string[] = [];

      if (defaultAriaDescribedBy) {
        ariaDescribedBy.push(defaultAriaDescribedBy);
      }

      if (helpText && !errorText && helpTextId) {
        ariaDescribedBy.push(helpTextId);
      } else if (errorText && errorTextId) {
        ariaDescribedBy.push(errorTextId);
      }

      return ariaDescribedBy.join(' ');
    }, [defaultAriaDescribedBy]);

    return (
      <components.Input
        {...restProps}
        className={classes}
        aria-describedby={combinedAriaDescribedBy}
      />
    );
  };

export const CustomSingleValue = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({ className, ...restProps }: SingleValueProps) {
    const classes = classNames('ndl-select-single-value', className, {
      'ndl-disabled': restProps.isDisabled,
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });
    return <components.SingleValue {...restProps} className={classes} />;
  };

/** Overrides that are part of the popover menu */

export const CustomMenu = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({ children, className, ...restProps }: MenuProps) {
    const { themeClassName } = useNeedleTheme();
    const classes = classNames('ndl-select-menu', className, themeClassName, {
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });
    return (
      <components.Menu {...restProps} className={classes}>
        {children}
      </components.Menu>
    );
  };

export const CustomMenuPortal = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: MenuPortalProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames(className, 'ndl-select-menu-portal', {
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });
    return (
      <components.MenuPortal {...restProps} className={classes}>
        {children}
      </components.MenuPortal>
    );
  };

export const CustomMenuList = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: MenuListProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames(className, 'ndl-select-menu-list', {
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    });
    return (
      <components.MenuList {...restProps} className={classes}>
        {children}
      </components.MenuList>
    );
  };

export const CustomOption = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  size,
}: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>) =>
  function Component({
    children,
    className,
    ...restProps
  }: OptionProps<OptionType, IsMulti, GroupType>) {
    const classes = classNames(className, 'ndl-select-option', {
      'ndl-disabled': restProps.isDisabled,
      'ndl-focused': restProps.isFocused,
      'ndl-is-multi': restProps.isMulti,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-selected': restProps.isSelected,
      'ndl-small': size === 'small',
    });
    const optionInnerProps = shiftOptionClickToMouseUp(restProps.innerProps);

    return (
      <components.Option
        {...restProps}
        className={classes}
        innerProps={optionInnerProps}
      >
        {restProps.isMulti === true && (
          <Checkbox
            isChecked={restProps.isSelected}
            ariaLabel={restProps.label}
            isDisabled={restProps.isDisabled}
            htmlAttributes={{
              'aria-hidden': true,
              disabled: true,
              onPointerDown: (e) => {
                e.preventDefault();
              },
              onPointerUp: (e) => {
                restProps.innerProps.onClick?.(e);
              },
              onTouchEnd: (e) => {
                restProps.innerProps.onClick?.(
                  e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>,
                );
              },
              onTouchStart: (e) => {
                e.preventDefault();
              },
              role: 'presentation',
              tabIndex: -1,
            }}
          />
        )}
        {children}
      </components.Option>
    );
  };
